import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import type { AuthClient, AuthUser, Permission } from './types.js';
import { hasAllPermissions, hasAnyPermission, hasPermission } from './permissions.js';

/**
 * Utility matching `react-oidc-context.hasAuthParams()` — returns true when
 * the current URL has the OIDC redirect-callback query/hash params. Used by
 * sign-in pages to skip the auto-redirect when handling a callback.
 */
export function hasAuthParams(location: Location = window.location): boolean {
  const params = new URLSearchParams(location.search || location.hash.replace(/^#/, ''));
  return (
    params.has('code') || params.has('error') || params.has('state') || params.has('session_state')
  );
}

/**
 * Reactive auth state exposed to React consumers.
 *
 * Surface mirrors `react-oidc-context`'s `AuthContextProps` closely enough
 * that timesheet's existing call sites can switch imports with minimal
 * code changes (D-F3 cutover):
 *   - `user` carries `access_token` so existing axios + token-claim parsing keeps working
 *   - `isLoading` / `isAuthenticated` booleans alongside our `status` enum
 *   - `signinRedirect` / `signoutRedirect` aliases for `signIn` / `signOut`
 *   - `events` exposes the addAccessTokenExpired hook the timesheet
 *     `useAuthEventHandler` listens on
 */
export type AuthState = {
  user: AuthUser | null;
  status: 'loading' | 'authenticated' | 'unauthenticated';
  isLoading: boolean;
  isAuthenticated: boolean;
  /** Last error from a sign-in/refresh/sign-out attempt. */
  error: Error | null;
  activeNavigator: 'signinRedirect' | 'signoutRedirect' | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  /** Alias of signIn — matches react-oidc-context naming. */
  signinRedirect: () => Promise<void>;
  /** Alias of signOut — matches react-oidc-context naming. */
  signoutRedirect: () => Promise<void>;
  events: {
    addAccessTokenExpired: (listener: () => void) => () => void;
    removeAccessTokenExpired: (listener: () => void) => void;
  };
};

const AuthContext = createContext<AuthState | null>(null);

export type AuthProviderProps = {
  client: AuthClient;
  children: ReactNode;
};

export function AuthProvider({ client, children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthState['status']>('loading');
  const [error, setError] = useState<Error | null>(null);
  const [activeNavigator, setActiveNavigator] = useState<AuthState['activeNavigator']>(null);
  const tokenExpiredListenersRef = useRef<Set<() => void>>(new Set());

  useEffect(() => {
    let cancelled = false;
    // If the current URL carries OIDC callback params, complete the redirect
    // flow first. Mirrors react-oidc-context's auto-handling.
    const initialLoad = async (): Promise<AuthUser | null> => {
      if (typeof window !== 'undefined' && hasAuthParams()) {
        try {
          return await client.handleCallback();
        } catch (e) {
          // Surface the real reason instead of silently dropping to
          // unauthenticated (e.g. id_token nonce/state validation failing
          // after a 200 token exchange, or stale oidc.* storage entries).
          const err = e instanceof Error ? e : new Error(String(e));
          // eslint-disable-next-line no-console
          console.error('[@iplan/auth] OIDC callback failed:', err);
          if (!cancelled) setError(err);
          return null;
        }
      }
      return client.getUser();
    };
    void initialLoad()
      .then((u) => {
        if (cancelled) return;
        setUser(u);
        setStatus(u ? 'authenticated' : 'unauthenticated');
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('unauthenticated');
      });
    const unsubscribe = client.onUserChanged((u) => {
      if (cancelled) return;
      setUser(u);
      setStatus(u ? 'authenticated' : 'unauthenticated');
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [client]);

  const signIn = useCallback(async () => {
    setActiveNavigator('signinRedirect');
    setError(null);
    try {
      await client.signIn();
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
      throw e;
    } finally {
      setActiveNavigator(null);
    }
  }, [client]);
  const signOut = useCallback(async () => {
    setActiveNavigator('signoutRedirect');
    setError(null);
    try {
      await client.signOut();
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
      throw e;
    } finally {
      setActiveNavigator(null);
    }
  }, [client]);

  // Wire each React-level listener through the AuthClient's
  // onAccessTokenExpired so the UserManager's underlying event reaches
  // the consumer. Each call returns a per-listener unsubscribe; we track
  // both maps so removeAccessTokenExpired can also disconnect cleanly.
  const tokenExpiredUnsubscribersRef = useRef<Map<() => void, () => void>>(new Map());
  const events = useMemo<AuthState['events']>(
    () => ({
      addAccessTokenExpired: (listener) => {
        tokenExpiredListenersRef.current.add(listener);
        const unsubscribe = client.onAccessTokenExpired(listener);
        tokenExpiredUnsubscribersRef.current.set(listener, unsubscribe);
        return () => {
          unsubscribe();
          tokenExpiredListenersRef.current.delete(listener);
          tokenExpiredUnsubscribersRef.current.delete(listener);
        };
      },
      removeAccessTokenExpired: (listener) => {
        const unsubscribe = tokenExpiredUnsubscribersRef.current.get(listener);
        unsubscribe?.();
        tokenExpiredListenersRef.current.delete(listener);
        tokenExpiredUnsubscribersRef.current.delete(listener);
      },
    }),
    [client],
  );

  const value = useMemo<AuthState>(
    () => ({
      user,
      status,
      isLoading: status === 'loading',
      isAuthenticated: status === 'authenticated',
      error,
      activeNavigator,
      signIn,
      signOut,
      signinRedirect: signIn,
      signoutRedirect: signOut,
      events,
    }),
    [user, status, error, activeNavigator, signIn, signOut, events],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}

export function usePermissions() {
  const { user } = useAuth();
  return useMemo(
    () => ({
      has: (p: Permission) => hasPermission(user, p),
      hasAny: (ps: readonly Permission[]) => hasAnyPermission(user, ps),
      hasAll: (ps: readonly Permission[]) => hasAllPermissions(user, ps),
    }),
    [user],
  );
}

/**
 * Reactive access-token hook. Subscribes to the bearerTokenCache so React
 * surfaces re-render when the token changes (sign-in/refresh/sign-out).
 *
 * For non-React consumers (e.g. axios interceptors), import `bearerTokenCache`
 * directly from `@iplan/auth` and call `.getToken()`.
 */
import { bearerTokenCache } from './bearer-token-cache.js';

export function useAccessToken(): string | null {
  const [token, setToken] = useState<string | null>(bearerTokenCache.getToken());
  useEffect(() => bearerTokenCache.subscribe(setToken), []);
  return token;
}
